<template>

  <v-container fluid class="mt-3 page vehicle-lot-item-edit">

    <!-- (data loading) -->
    <template v-if="isDataLoading">
      <v-row>
        <v-col class="mt-4 d-flex align-center justify-center" style="height:120px">
          <v-progress-circular indeterminate :size="50" color="primary" />
        </v-col>
      </v-row>
    </template>

    <template v-else-if="vehicleLotToEdit">

      <!-- Breadcrumb -->
      <v-row class="mt-2 mb-6">
        <v-col cols="12" class="d-flex align-center justify-start page-breadcrumb-new">
          <!-- (breadcrumb) -->
          <v-icon small class="mr-2">mdi-parking</v-icon>
          <div>Vehicle Lot</div>
          <div class="separator">/</div>
          <div class="active-item"><span class="label-prefix">Edit Vehicle Lot:</span> {{ vehicleLotToEdit.name }}</div>
        </v-col>
      </v-row>

      <!-- Page Actions (cancel, save) -->
      <v-toolbar flat class="item-controls item-editor-controls">
        <v-btn width="85" color="gray" text-color="black" @click="cancel" :disabled="isDataSaving">Close</v-btn>
        <v-spacer />
        <div class="saved-message-text" v-if="dataSavedMessage">
          <v-icon color="green" class="mr-2 pb-1">mdi-check-bold</v-icon>
          <span>{{ dataSavedMessage }}</span>
        </div>
        <v-btn width="120" color="primary" @click="save" v-else>
          <v-progress-circular indeterminate :size="18" color="white" v-if="isDataSaving" />
          <span v-else>Save</span>
        </v-btn>
      </v-toolbar>

      <!-- Item Edit Form -->
      <v-container fluid class="mt-4 mb-4 pb-3 page-content">
        <!-- Name -->
        <v-row class="mt-0">
          <v-col cols="12" class="pt-1">
            <div class="title">Name</div>
          </v-col>
        </v-row>
        <v-row class="mt-4">
          <v-col cols="12" xs="12" sm="12" md="7" lg="5" class="pt-0">
            <v-text-field
                outlined
                :disabled="isDataSaving"
                :rules="$root.rules.fieldRequired"
                v-model.trim="vehicleLotToEdit.name" />
          </v-col>
        </v-row>

        <!-- Address -->
        <v-row class="mt-4">
          <v-col cols="12">
            <div class="title">Address</div>
          </v-col>
        </v-row>
        <!-- (street 1) -->
        <v-row align="center" class="mt-2">
          <v-col cols="12" xs="12" sm="12" md="3" lg="1">
            <span class="label">Street 1:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="10" md="5" lg="4">
            <v-text-field
                outlined
                :disabled="isDataSaving"
                :rules="$root.rules.fieldRequired"
                v-model="vehicleLotToEdit.street_1" />
          </v-col>
        </v-row>
        <!-- (street 2) -->
        <v-row align="center" class="mt-1 mb-3">
          <v-col cols="12" xs="12" sm="12" md="3" lg="1">
            <span class="label">Street 2:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="10" md="5" lg="4">
            <v-text-field
                outlined
                hide-details
                :disabled="isDataSaving"
                v-model="vehicleLotToEdit.street_2" />
          </v-col>
        </v-row>
        <!-- (city) -->
        <v-row align="center" class="mt-2">
          <v-col cols="12" xs="12" sm="12" md="3" lg="1">
            <span class="label">City:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="10" md="5" lg="3">
            <v-text-field
                outlined
                :disabled="isDataSaving"
                :rules="$root.rules.fieldRequired"
                v-model="vehicleLotToEdit.city" />
          </v-col>
        </v-row>
        <!-- (state) -->
        <v-row align="center" class="mt-2">
          <v-col cols="12" xs="12" sm="12" md="3" lg="1">
            <span class="label">State:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="6" md="4" lg="3">
            <v-select
                outlined
                :items="stateValueList"
                item-text="label"
                item-value="code"
                :disabled="isDataSaving"
                v-model="vehicleLotToEdit.state" />
          </v-col>
        </v-row>
        <!-- (zip) -->
        <v-row align="center" class="mt-2">
          <v-col cols="12" xs="12" sm="12" md="3" lg="1">
            <span class="label">Zip:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="5" md="3" lg="2">
            <v-text-field
                outlined
                :disabled="isDataSaving"
                :rules="$root.rules.fieldRequired"
                v-model="vehicleLotToEdit.zip" />
          </v-col>
        </v-row>
      </v-container>

    </template>

  </v-container>

</template>

<script>
export default {
  name: 'VehicleLotItemEditPage',

  props: {
    vehicleLotId: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      isDataLoading: false,
      dataLoadingError: null,
      isDataSaving: false,
      dataSavingError: null,
      dataSavedMessage: null,
      vehicleLotToEdit: null
    }
  },

  computed: {
    stateValueList () {
      return this.$store.getters['app/datasetUsStates']
    }
  },

  async beforeMount () {
    // Ensure itemToEdit is loaded
    if (!this.vehicleLotToEdit) {
      this.isDataLoading = true

      try {
        this.vehicleLotToEdit = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `vehicle-lot/${this.vehicleLotId}`
        })
      } catch (err) {
        this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    }
  },

  methods: {
    cancel () {
      this.$root.goTo('vehicle-lot-item') // go to item view page
    },

    async save () {
      this.isDataSaving = true

      try {
        this.vehicleLotToEdit = await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `vehicle-lot/${this.vehicleLotId}`,
          opts: {
            bodyData: this.vehicleLotToEdit
          }
        })

        // Flash saved message
        this.dataSavedMessage = 'Changes Saved!'
        setTimeout(() => {
          this.dataSavedMessage = null
        }, 4000)
      } catch (err) {
        this.dataSavingError = err.message || err.text || err
      } finally {
        this.isDataSaving = false
      }
    }
  }
}
</script>

<style scoped>
.vehicle-lot-item-edit .saved-message-text {
    font-size: 1.0rem;
    color: #4CAF50;
    font-weight: 400;
  }
</style>
